import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion, useReducedMotion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FiMail, FiGlobe, FiSend, FiLoader, FiUser, FiEdit3 } from "react-icons/fi";
import { ApiHandler } from "../../../helper/ApiHandler";
import contact from "../../../assest/images/contact.png";

/* ------------------------------------------------------------------ */
/*  Contact section — recolored to match the About section palette     */
/*  (warm amber / deep brown) + same image parallax + float motion     */
/* ------------------------------------------------------------------ */
const CONTACT_STYLES = `
  @keyframes ctc-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }
  .ctc-float { animation: ctc-float 4s ease-in-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    .ctc-float { animation: none !important; }
  }
`;

export default function Contact() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const imgRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  // On mobile, the scroll-triggered reveal (opacity/x/y animations driven by
  // useInView) was sometimes leaving the section stuck at opacity:0 until
  // the user scrolled back and forth a few times. Simplest reliable fix:
  // skip the reveal animation entirely on small screens and just show the
  // final state immediately.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const skipAnim = isMobile || prefersReducedMotion;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const result = await ApiHandler(
        "/contact_us.php",
        "POST",
        formData,
        undefined,
        dispatch,
        navigate
      );

      if (result?.data?.status === "1") {
        toast.success(result.data.msg);
        setFormData({ name: "", email: "", message: "" });
        navigate("/");
      } else {
        toast.error(result?.data?.msg || "Something went wrong.");
      }
    } catch (err) {
      toast.error("Unable to send message right now.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* Same mouse-parallax tilt used on the About section image (desktop only, harmless on touch) */
  const handleMouseMove = (e) => {
    if (prefersReducedMotion) return;
    const el = imgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / 30;
    const y = (e.clientY - rect.top - rect.height / 2) / 30;
    el.style.transform = `translate(${x}px, ${y}px)`;
  };

  const resetTransform = () => {
    const el = imgRef.current;
    if (el) el.style.transform = "translate(0px, 0px)";
  };

  return (
    <section
      id="contactID"
      className="relative py-16 md:py-24 overflow-hidden border-t border-[#F5DFC5]"
      style={{
        background:
          "radial-gradient(90% 70% at 85% 0%, #FFF4E5 0%, transparent 55%), radial-gradient(80% 60% at 5% 100%, #FEF3E5 0%, transparent 55%), #FFFBF5",
      }}
    >
      <style>{CONTACT_STYLES}</style>

      {/* Ambient glow — same treatment as About */}
      <div
        aria-hidden="true"
        className="absolute top-0 left-[-10%] w-[500px] h-[400px] opacity-[0.12] blur-[100px] pointer-events-none"
        style={{ background: "radial-gradient(circle, #D97706, transparent 70%)" }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left Side: Image with parallax + floating badge */}
          <div className="relative order-2 lg:order-1" ref={ref}>
            {/* Background Decorative Box */}
            <motion.div
              initial={skipAnim ? false : { x: -80, opacity: 0 }}
              animate={skipAnim ? { x: 0, opacity: 1 } : inView ? { x: 0, opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="absolute -bottom-8 w-40 sm:w-48 h-40 sm:h-48 -z-0 hidden md:block"
              style={{
                background: "linear-gradient(145deg, #D97706, #EA580C)",
                borderRadius: "2.5rem 1rem 2.5rem 1rem",
              }}
            ></motion.div>

            {/* Main Image with mouse-parallax tilt */}
            <motion.div
              initial={skipAnim ? false : { opacity: 0, y: 20 }}
              animate={skipAnim ? { opacity: 1, y: 0 } : inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              onMouseMove={handleMouseMove}
              onMouseLeave={resetTransform}
              className="relative left-0 sm:left-8 z-10 overflow-hidden shadow-[0_30px_80px_-25px_rgba(28,20,16,0.25)] border-[3px] sm:border-8 border-white bg-white"
              style={{ borderRadius: "2rem 1rem 2rem 1rem" }}
            >
              <img
                ref={imgRef}
                src={contact}
                alt="Contact MyAd360"
                className="w-full h-[280px] sm:h-[350px] md:h-[420px] object-cover transition-transform duration-200 ease-out"
              />
              {/* Warm overlay, same as About */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: "linear-gradient(180deg, transparent 55%, rgba(28,20,16,0.4) 100%)" }}
              />

              {/* Network Badge — recolored + floating like About's milestone cards */}
              <motion.div
                initial={skipAnim ? false : { x: 20, opacity: 0 }}
                animate={skipAnim ? { x: 0, opacity: 1 } : inView ? { x: 0, opacity: 1 } : {}}
                transition={{ delay: 0.4 }}
                className="ctc-float absolute bottom-4 sm:bottom-6 right-0 bg-[#1C1410] py-3 sm:py-4 px-5 sm:px-6 shadow-2xl border-l-4 border-[#D97706]"
                style={{ borderRadius: "1rem 0 0 1rem" }}
              >
                <p className="text-[#F59E0B] text-[10px] font-bold uppercase tracking-widest mb-0.5">Network</p>
                <p className="text-white font-bold text-xs sm:text-sm">MyAd360 Global Network</p>
              </motion.div>
            </motion.div>
          </div>

          {/* Right Side: Form & Content */}
          <motion.div
            initial={skipAnim ? false : { opacity: 0, x: 50 }}
            animate={skipAnim ? { opacity: 1, x: 0 } : inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="order-1 lg:order-2"
          >
            <div className="mb-8 sm:mb-10">
              <div className="flex items-center w-full gap-4 mb-4">
                <span
                  className="hidden sm:inline-block h-[2px] flex-1"
                  style={{
                    background: "linear-gradient(to left, #EA580C, transparent)",
                  }}
                />
                <span className="text-xs font-bold tracking-widest uppercase text-[#92400E] whitespace-nowrap">
                  Get In Touch
                </span>
                <span
                  className="hidden sm:inline-block h-[2px] flex-1"
                  style={{
                    background: "linear-gradient(to right, #EA580C, transparent)",
                  }}
                />
              </div>
              <h2
            className="ov-display text-3xl sm:text-4xl lg:text-[3.2rem] text-[#1C1410] leading-[1.12]"
            style={{ fontWeight: 600 }}
          >
                Let's start a {" "}
                <span className="relative inline-block whitespace-nowrap">
              <span
                className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#EA580C]"
                style={{ fontStyle: "italic", fontWeight: 500 }}
              >
                conversation
              </span>
              <svg
                className="absolute left-0 -bottom-1.5 w-full"
                height="9"
                viewBox="0 0 200 9"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M2,6 C40,1 70,8 100,4 C130,0 160,7 198,3"
                  fill="none"
                  stroke="url(#ovUnderline)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="ovUnderline" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#D97706" />
                    <stop offset="100%" stopColor="#EA580C" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
              </h2>
              <p className="text-[#4D3E30] mt-4 text-base sm:text-lg max-w-md">
                Tell us what you're building and we'll get back to you within one business day.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="relative">
                  <FiUser className="absolute left-5 top-5 text-[#A08E7A]" />
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    placeholder="Your Name*"
                    onChange={handleChange}
                    className="w-full pl-12 pr-6 py-4 bg-white border border-[#F0DCC8] rounded-xl focus:border-[#D97706] outline-none transition-all shadow-sm text-sm sm:text-base"
                  />
                </div>

                <div className="relative">
                  <FiMail className="absolute left-5 top-5 text-[#A08E7A]" />
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    placeholder="Your Email*"
                    onChange={handleChange}
                    className="w-full pl-12 pr-6 py-4 bg-white border border-[#F0DCC8] rounded-xl focus:border-[#D97706] outline-none transition-all shadow-sm text-sm sm:text-base"
                  />
                </div>
              </div>

              <div className="relative">
                <FiEdit3 className="absolute left-5 top-5 text-[#A08E7A]" />
                <textarea
                  name="message"
                  placeholder="Tell us about your project..."
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full pl-12 pr-6 py-4 h-32 bg-white border border-[#F0DCC8] rounded-xl focus:border-[#D97706] outline-none resize-none transition-all shadow-sm text-sm sm:text-base"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="group w-full md:w-auto px-10 py-4 bg-gradient-to-r from-[#D97706] to-[#B45309] hover:from-[#7C3A0E] hover:to-[#D97706] text-white font-bold rounded-xl shadow-[0_16px_34px_-14px_rgba(217,119,6,0.5)] transition-all duration-300 flex items-center justify-center gap-3 uppercase tracking-wider text-sm"
              >
                {isSubmitting ? (
                  <FiLoader className="animate-spin" />
                ) : (
                  <>
                    Send Message
                    <FiSend className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </>
                )}
              </button>
            </form>

            {/* Bottom Contact Details */}
            <div className="mt-10 pt-8 border-t border-[#F0DCC8] grid grid-cols-1 sm:grid-cols-2 gap-6">
              <a href="mailto:info@myad360.com" className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-lg bg-white shadow-md flex items-center justify-center text-[#D97706] group-hover:bg-[#D97706] group-hover:text-white transition-all flex-shrink-0">
                  <FiMail size={20} />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-[#8A7560] tracking-widest">Email Us</p>
                  <p className="text-[#1C1410] font-bold text-sm break-all">info@myad360.com</p>
                </div>
              </a>

              <a href="https://www.myad360.com" target="_blank" rel="noreferrer" className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-lg bg-white shadow-md flex items-center justify-center text-[#D97706] group-hover:bg-[#D97706] group-hover:text-white transition-all flex-shrink-0">
                  <FiGlobe size={20} />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-[#8A7560] tracking-widest">Website</p>
                  <p className="text-[#1C1410] font-bold text-sm">www.myad360.com</p>
                </div>
              </a>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}